import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserAvatar } from "@/components/UserAvatar";
import { StartChatButton } from "@/components/StartChatButton";
import { jobPoster } from "@/components/JobRow";
import { api, ago, formatApiError } from "@/lib/api";
import { isAdmin, isProfessional, professionOf } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function Applicants({ jobId }) {
  const [apps, setApps] = useState(null);
  useEffect(() => {
    api.get(`/jobs/${jobId}/applications`).then(({ data }) => setApps(data)).catch(() => setApps([]));
  }, [jobId]);
  if (apps === null) return <p className="text-sm text-slate-500">Loading applications...</p>;
  if (!apps.length) return <p className="text-sm text-slate-500" data-testid="job-no-applicants">No applications yet.</p>;
  return apps.map((a, i) => {
    const u = a.applicant || a.user || a.author;
    const phone = !u?.privacy?.hide_phone && (u?.whatsapp || u?.contact_number);
    return (
      <div key={a.id || i} className="rounded-xl border p-4" data-testid={`applicant-${u?.id || i}`}>
        <div className="flex flex-wrap items-center gap-3">
          <UserAvatar user={u} size="sm" />
          <Link to={`/profile/${u?.id}`} className="min-w-0 flex-1 text-sm font-semibold text-tm-green hover:underline">{u?.full_name}<span className="block text-xs font-normal text-slate-500">{professionOf(u)} · {ago(a.created_at)}</span></Link>
          <StartChatButton userId={u?.id} label="Respond" variant="default" className="bg-tm-green hover:bg-tm-green/90" testid={`applicant-respond-${u?.id || i}`} />
          {phone && <Button asChild variant="outline" size="sm" className="rounded-full" data-testid={`applicant-whatsapp-${u?.id || i}`}><a href={`https://wa.me/91${phone}`} target="_blank" rel="noreferrer"><Phone className="mr-1 h-3.5 w-3.5" />WhatsApp</a></Button>}
        </div>
        {a.message && <p className="mt-3 text-sm text-slate-700">{a.message}</p>}
      </div>
    );
  });
}

export const JobDetailDialog = ({ job, onClose, onChanged }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [applied, setApplied] = useState(false);
  const poster = job && jobPoster(job);
  const posterId = poster?.id || job?.posted_by || job?.author_id;
  const isOwner = user && job && (user.id === posterId || isAdmin(user));
  const isLead = job?.responses != null && !posterId;

  useEffect(() => {
    setApplied(!!job?.applied);
    setMessage("");
  }, [job]);

  const apply = async () => {
    if (!user) return navigate("/login");
    try {
      await api.post(`/jobs/${job.id}/apply`, { message });
      toast.success("Application sent");
      setApplied(true);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Job deleted");
      onClose();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="job-detail-dialog">
        {job && (
          <>
            <DialogHeader><DialogTitle className="font-display text-xl text-tm-green" data-testid="job-detail-title">{job.title}</DialogTitle></DialogHeader>
            {poster && <div className="flex items-center gap-3"><UserAvatar user={poster} size="sm" /><Link to={`/profile/${posterId}`} className="text-sm font-semibold hover:underline">{poster.full_name}</Link><span className="text-xs text-slate-500">{ago(job.created_at)}</span></div>}
            <div className="flex flex-wrap gap-2 text-xs">
              {[job.category, job.type, job.work_mode, job.city, job.salary || job.budget, job.experience].filter(Boolean).map((t) => <span key={t} className="rounded-full bg-tm-muted px-3 py-1 text-tm-green">{t}</span>)}
            </div>
            <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">{job.description}</p>
            {isLead ? (
              <p className="rounded-xl bg-tm-muted p-4 text-sm text-slate-700" data-testid="lead-info">This is a client lead. Respond to it from the Taxmandi app.</p>
            ) : isOwner ? (
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between"><h4 className="font-display font-bold text-tm-green">Applications</h4>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={remove} data-testid="job-delete-button"><Trash2 className="mr-1.5 h-4 w-4" />Delete</Button></div>
                <Applicants jobId={job.id} />
              </div>
            ) : applied ? (
              <p className="rounded-xl bg-emerald-50 p-4 text-sm font-medium text-emerald-800" data-testid="job-applied-message">You have applied to this job.</p>
            ) : user && !isProfessional(user) && user.account_type !== "STUDENT" ? (
              <div className="flex flex-wrap items-center gap-3 border-t pt-4" data-testid="job-client-contact">
                <p className="flex-1 text-sm text-slate-600">Interested in this firm? Contact them directly.</p>
                <StartChatButton userId={posterId} variant="default" className="bg-tm-green hover:bg-tm-green/90" testid="job-message-poster-button" />
              </div>
            ) : (
              <div className="space-y-3 border-t pt-4">
                <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Why are you a good fit? Mention relevant experience." data-testid="job-message-input" />
                <div className="flex gap-2">
                  <Button onClick={apply} className="flex-1 rounded-full bg-tm-green hover:bg-tm-green/90" data-testid="job-apply-button">{user ? "Apply now" : "Log in to apply"}</Button>
                  <StartChatButton userId={posterId} testid="job-message-poster-button" />
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
